import { JuiImageEntry, JuiImageRejection, JuiImageSort } from './jui-image-capture.model';

export interface ResizeBounds {
  maxEdgePx: number;
  quality: number;
}

let nextImageId = 0;

export function createImageId(): string {
  nextImageId += 1;
  return `jui-image-${Date.now()}-${nextImageId}`;
}

export function computeScaledDimensions(
  width: number,
  height: number,
  maxEdgePx: number
): { width: number; height: number } {
  const longestEdge = Math.max(width, height);
  const scale = longestEdge > maxEdgePx ? maxEdgePx / longestEdge : 1;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export function matchesAccept(fileType: string, fileName: string, accept: string): boolean {
  const tokens = accept
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(token => token.length > 0);
  if (tokens.length === 0) {
    return true;
  }
  const type = fileType.toLowerCase();
  const name = fileName.toLowerCase();
  return tokens.some(token => {
    if (token.startsWith('.')) {
      return name.endsWith(token);
    }
    if (token.endsWith('/*')) {
      return type.startsWith(token.slice(0, -1));
    }
    return type === token;
  });
}

const ANDROID_14_CAMERA_AND_FILES_TYPE = 'text/plain';

export function pickerAccept(accept: string): string {
  return matchesAccept(ANDROID_14_CAMERA_AND_FILES_TYPE, '', accept)
    ? accept
    : `${accept},${ANDROID_14_CAMERA_AND_FILES_TYPE}`;
}

export function checkFile(file: File, accept: string, maxBytes: number): JuiImageRejection | null {
  if (!matchesAccept(file.type, file.name, accept)) {
    return { fileName: file.name, reason: 'file-type' };
  }
  if (file.size > maxBytes) {
    return { fileName: file.name, reason: 'file-too-large' };
  }
  return null;
}

export function dataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

export function remainingSlots(current: number, maxImages: number | null): number {
  return maxImages === null ? Infinity : Math.max(maxImages - current, 0);
}

export function placeImages(existing: JuiImageEntry[], added: JuiImageEntry[], sort: JuiImageSort): JuiImageEntry[] {
  return sort === 'desc' ? [...[...added].reverse(), ...existing] : [...existing, ...added];
}

export async function resizeImage(image: Blob, bounds: ResizeBounds): Promise<string> {
  const bitmap = await createImageBitmap(image);
  try {
    const { width, height } = computeScaledDimensions(bitmap.width, bitmap.height, bounds.maxEdgePx);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('canvas 2d context unavailable');
    }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', bounds.quality);
  } finally {
    bitmap.close();
  }
}
